import React, {useCallback, useRef} from "react";
import {css} from "@emotion/react";
import {Link} from "react-router-dom";
import {styled, Text, theme} from "@reside/ui";

import {getResidentProfileHref} from "./admissionRoutes";

/** Clicks on these (or their descendants) do not trigger `admissionHref` navigation. */
const CARD_NAV_IGNORE_SELECTOR = [
  "a",
  "button",
  "input",
  "textarea",
  "select",
  "option",
  "label",
  '[role="button"]',
  '[role="menuitem"]',
  '[role="menuitemcheckbox"]',
  '[role="menuitemradio"]',
  '[role="tab"]',
  '[role="checkbox"]',
  '[role="switch"]',
  '[role="combobox"]',
  '[role="listbox"]',
  '[role="option"]',
  '[role="dialog"]',
  '[role="alertdialog"]',
  '[aria-modal="true"]',
  "[data-placement]",
  "[data-popper-placement]",
  "[data-dashboard-card-stop]",
  '[contenteditable="true"]',
].join(",");

const isCardNavIgnored = (target: EventTarget | null) =>
  target instanceof Element &&
  Boolean(target.closest(CARD_NAV_IGNORE_SELECTOR));

/** Drag-to-select (e.g. ID: code) still fires `click` on mouseup; skip nav so text can be copied. */
const DRAG_CLICK_THRESHOLD_PX = 4;

const hasSelectionWithin = (container: HTMLElement) => {
  const selection = window.getSelection();
  if (
    !selection ||
    selection.isCollapsed ||
    selection.toString().length === 0
  ) {
    return false;
  }
  const {anchorNode, focusNode} = selection;
  return (
    (anchorNode != null && container.contains(anchorNode)) ||
    (focusNode != null && container.contains(focusNode))
  );
};

/** Marker for card-hover UI (⋯ menu, resident-experience control) without Emotion component selectors. */
const StyledDashboardCell = styled.div<{
  withWarningBorder?: boolean;
  $admissionClickable?: boolean;
}>`
  background-color: ${({theme}) => theme.color.white};
  border-radius: 4px;
  border: 1px solid ${theme.color.borderGray};
  margin-bottom: 15px;
  padding: 15px;
  transition: background-color 0.12s ease;

  ${({$admissionClickable}) =>
    $admissionClickable &&
    css`
      cursor: pointer;
    `}

  ${({withWarningBorder, theme}) =>
    withWarningBorder
      ? css`
          &:hover {
            background-color: ${theme.color.orange100}06;
          }
        `
      : css`
          &:hover {
            background-color: rgb(249, 249, 249);
          }
        `}

  ${({withWarningBorder}) =>
    withWarningBorder &&
    css`
      border: 2px solid ${theme.color.orange100}80;
      background-color: ${theme.color.orange100}20;
      box-shadow: 0 1px 5px 0 ${theme.color.orange100}80;
    `}
`;

export type DashboardCellProps = Omit<
  React.ComponentProps<typeof StyledDashboardCell>,
  "onClick" | "$admissionClickable"
> & {
  /** When set, clicking non-interactive areas opens this route in a new tab. */
  admissionHref?: string;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
};

export const DashboardCell = ({
  admissionHref,
  onClick,
  ...rest
}: DashboardCellProps) => {
  const pointerDownRef = useRef<{x: number; y: number} | null>(null);

  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    pointerDownRef.current = {x: e.clientX, y: e.clientY};
  }, []);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      onClick?.(e);
      if (e.defaultPrevented) {
        return;
      }
      if (!admissionHref) {
        return;
      }
      const t = e.target;
      /** Portaled menus (e.g. ⋯) bubble through React but `target` is outside this node in the DOM. */
      if (t instanceof Node && !e.currentTarget.contains(t)) {
        return;
      }
      if (isCardNavIgnored(t)) {
        return;
      }
      // Let users copy admission id / resident name without opening the card.
      if (hasSelectionWithin(e.currentTarget)) {
        return;
      }
      const down = pointerDownRef.current;
      if (down) {
        const dx = Math.abs(e.clientX - down.x);
        const dy = Math.abs(e.clientY - down.y);
        if (dx > DRAG_CLICK_THRESHOLD_PX || dy > DRAG_CLICK_THRESHOLD_PX) {
          return;
        }
      }
      window.open(admissionHref, "_blank", "noopener,noreferrer");
    },
    [admissionHref, onClick],
  );

  return (
    <StyledDashboardCell
      data-dashboard-cell=""
      $admissionClickable={Boolean(admissionHref)}
      {...rest}
      onMouseDown={admissionHref ? handleMouseDown : undefined}
      onClick={admissionHref ? handleClick : onClick}
    />
  );
};

export const CellHeader = styled.div`
  display: flex;
  flex-grow: 1;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 15px;
  > :nth-child(2) {
    margin-right: auto;
  }
`;

/** Keeps trailing header actions right-aligned when admission id sits below the name. */
export const CellHeaderSpacer = styled.div`
  flex: 1;
  min-width: 0;
`;

/** Resident name + admission id (ID: code) stacked before the rest of the card body. */
export const CellIdentityBlock = styled.div`
  margin-bottom: 12px;
`;

/** Progress row: bar fills the meta row. */
export const CellMetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  column-gap: 12px;
  row-gap: 4px;
  margin-bottom: 12px;
`;

/** Progress fills the meta row. */
export const CellMetaProgressSlot = styled.div`
  flex: 1;
  min-width: 0;
  margin-right: 8px;
`;

/** Bottom-of-card “Last update: …” (prefix muted, time emphasized). */
export const CellLastUpdateRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  column-gap: 4px;
  font-size: 12px;
  line-height: 16px;
  font-weight: ${({theme}) => theme.fontWeight.medium};
`;

export const CellLastUpdatePrefix = styled.span`
  color: ${({theme}) => theme.color.gray100};
`;

export const CellLastUpdateTime = styled.span`
  color: ${({theme}) => theme.color.darkBlue70};

  time {
    color: inherit;
    font: inherit;
    font-weight: inherit;
  }
`;

export const CellTextBig = styled.div`
  font-size: 17px;
  font-weight: ${({theme}) => theme.fontWeight.medium};
  line-height: 20px;
  word-break: break-all;
  margin-bottom: 4px;
  * {
    color: ${({theme}) => theme.color.darkBlue100};
    font-weight: inherit;
  }
`;

/** Resident name on dashboard cards: slight text color shift on hover. */
export const CellResidentName = styled(CellTextBig)`
  align-self: flex-start;
  max-width: 100%;

  * {
    transition: color 0.12s ease;
  }

  &:hover * {
    color: ${({theme}) => theme.color.darkBlue90};
  }
`;

const ResidentNameLink = styled(Link)`
  color: inherit;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

export type CellResidentNameLinkProps = Readonly<{
  name: string;
  residentId?: string | null;
}> &
  Omit<React.ComponentProps<typeof CellResidentName>, "children">;

/** Resident name link: navigates to the linked resident profile when residentId is present. */
export const CellResidentNameLink = ({
  name,
  residentId,
  ...rest
}: CellResidentNameLinkProps) => (
  <CellResidentName {...rest}>
    {residentId ? (
      <ResidentNameLink to={getResidentProfileHref(residentId)}>
        <Text>{name}</Text>
      </ResidentNameLink>
    ) : (
      <Text>{name}</Text>
    )}
  </CellResidentName>
);

export const CellTextSmall = styled.span`
  color: ${({theme}) => theme.color.gray100};
  font-size: 14px;
  font-weight: ${({theme}) => theme.fontWeight.medium};
  line-height: 16px;
`;

export const CellFooter = styled.div`
  display: flex;
  margin-top: 10px;
  align-items: center;
  justify-content: space-between;
`;

export const Left = styled.div`
  display: flex;
`;

export const Right = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  column-gap: 8px;
  row-gap: 4px;
`;
